/**
 * The streaming terrain: quadtree selection, a worker pool, the LRU, and the scene graph.
 *
 * Per frame, in order:
 *   1. adopt as many finished tiles as this frame's upload budget allows;
 *   2. ask the {@link LodSelector} which tiles this view wants;
 *   3. pin those that are already resident so the LRU cannot evict them;
 *   4. queue the ones that are missing, nearest first, and cancel anything queued that has
 *      fallen out of view;
 *   5. show the resident tiles, substituting a coarser ancestor wherever a tile is still in
 *      flight, so the ground is never simply absent;
 *   6. trim the LRU back inside its byte budget.
 *
 * The only work here that scales with the *world* is step 2; everything else scales with what
 * is on screen. Nothing in this file decodes, meshes, or blocks — a tile arrives as a message
 * carrying transferred buffers, and adopting it is a `BufferGeometry` and a `Texture` wrapped
 * around memory that already exists.
 *
 * ## Why adoption is rationed
 *
 * Worker messages do not arrive politely spaced out. Two workers with eight concurrent fetches
 * each will land a dozen finished tiles between one frame and the next, and if all twelve are
 * adopted in that gap then one frame carries a dozen vertex-buffer creations, a dozen texture
 * uploads with mipmap generation, and — the expensive part — a dozen first-use pipeline lookups.
 * That is a 40 ms frame in the middle of an otherwise flat 120 fps, and it is most of what a
 * 1 %-low figure is measuring. So finished tiles wait in {@link TerrainSystem.pending} and at
 * most {@link TerrainSettings.tileUploadsPerFrame} of them are adopted per frame. Nothing is
 * lost — a tile a frame or two late is invisible next to the network latency that fetched it —
 * and the streaming cost becomes a flat line instead of a comb.
 *
 * ## What a tile is allowed to keep
 *
 * three has no way to release a vertex buffer's CPU-side copy once it is on the GPU: the
 * `onUploadCallback` hook that does it on the classic WebGL renderer is commented out in both
 * the WebGPU backend and its WebGL2 fallback. So whatever a tile hands to a `BufferAttribute`
 * stays on the JS heap for as long as the tile is resident, and with a few hundred tiles that
 * arithmetic decides whether a long flight fits in memory. Two of the five attributes — texture
 * coordinates and the triangle list — are identical for every tile in the world, so they are
 * built once and shared (see `mesher.ts`); each tile still gets its own `BufferAttribute` object
 * over them, because three keys GPU buffers and their disposal by attribute object.
 *
 * ## Tiles are rigid bodies
 *
 * A tile is meshed once, in its own tangent frame at its own centre, with the Earth's curvature
 * already in the vertices. Placing it is therefore a position and a quaternion, computed
 * through {@link LocalFrame.transformFrom}. That is what makes a floating-origin rebase cost a
 * few hundred matrix updates instead of re-meshing the visible world, and it is why tiles at
 * different LODs still meet exactly along their shared edges.
 *
 * `updateFrame(alpha)`: yes — `updateFrame` does selection and placement. There is no
 * `updateFixed`: the simulation reads ground height through {@link sampleGroundHeightM}, which
 * is a lookup, not an update.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Group,
  Mesh,
  Quaternion,
  Sphere,
  Texture,
  Vector3,
  type Material,
} from 'three';
import type { QualitySettings } from '../../core/quality.js';
import { clamp } from '../../core/math.js';
import { LocalFrame, type FloatingOrigin } from '../geodesy.js';
import { tileIndices, tileUvs } from './mesher.js';
import { LodSelector, type LodSelection, type LodSelectionParams } from './quadtree.js';
import { TileCache, type CacheableTile } from './tileCache.js';
import type { TileProvider } from './provider.js';
import {
  demWindow,
  keyX,
  keyY,
  keyZoom,
  makeDemWindow,
  tileKey,
  tileXToLongitude,
  tileYToLatitude,
} from './tileId.js';
import type {
  MeshTileRequest,
  MeshTileResponse,
  TileErrorResponse,
  TileWorkerResponse,
} from './tileMessages.js';

/**
 * Vertices along a tile edge. Odd, so the geomorph lattice has a centre sample.
 *
 * 65 rather than the more obvious 33, and the reason is draw calls rather than detail. A tile's
 * geometric error is its vertex spacing, so doubling the grid halves the error — which means
 * the screen-space-error rule is satisfied one zoom level *earlier* and the radius needing
 * maximum detail halves. Measured over San Francisco at the Medium tier: a 33-grid wanted about
 * 950 tiles at z14, comfortably past the 350 draw calls CLAUDE.md allows for the entire frame;
 * a 65-grid wants about 50, for the same number of triangles in a quarter of the submissions.
 * Bigger grids keep trading the same way until the vertex buffers stop fitting in the cache
 * budget, which is where this sits.
 */
const GRID_SIZE = 65;
/** How far a tile's edge curtain hangs down [m]. Covers the worst LOD mismatch at z14. */
const SKIRT_DEPTH_M = 260;

/** Ceiling on remembered "no data here" tiles, so the miss set cannot grow without limit. */
const MAX_REMEMBERED_MISSES = 4_096;

/**
 * What one resident tile costs [bytes] — a 256² texture plus a 65-grid's vertex buffers.
 *
 * Measured rather than derived: 480 tiles resident over San Francisco reported 290.3 MB, which is
 * 634 KB each. The arithmetic agrees — 256 KB of RGBA texel, ~142 KB of positions, normals, morph
 * heights and the height grid, and the rest in the decoded `ImageBitmap` the texture was uploaded
 * from — but the measurement is the number that matters, because it includes whatever the backend
 * keeps that we do not account for.
 */
const RESIDENT_TILE_BYTES = 634 * 1024;

/**
 * How many tiles a tier's cache budget can hold.
 *
 * `LodSelector.maxTiles` and `TileCache.maxBytes` are the same constraint stated in two units, and
 * they were free to disagree. They did: raising the tile cap to 480 to keep the horizon alive at
 * low altitude — where the quadtree, newly allowed past z15, spends its budget close in — put 290
 * MB of resident tiles against a 200 MB tier budget, because **the cache cannot evict a tile that
 * is on screen.** Selecting a tile is deciding to keep it, so the selection cap *is* the cache
 * floor, and choosing them independently means whichever is chosen larger silently wins.
 *
 * Deriving one from the other is the fix, and the direction matters: memory is the hard constraint
 * and coverage is what it buys, not the other way round. The bounds are a sanity rail rather than
 * a policy — below about a hundred tiles there is no horizon at any altitude, and past 512 no tier
 * has the budget.
 */
export function maxTilesForCacheBudget(tileCacheMB: number): number {
  return clamp(Math.floor((tileCacheMB * 1024 * 1024) / RESIDENT_TILE_BYTES), 96, 512);
}

/**
 * The image a disposed tile's texture is parked on. One 1×1 canvas for the whole session.
 *
 * Built lazily and shared, because it exists only to be a valid thing for a reference that should
 * not exist to point at — see the long note in {@link TerrainTile.dispose}. A canvas rather than a
 * raw `{data, width, height}` because the texture is a plain `Texture`, and the backend's upload
 * path for one wants a `CanvasImageSource`; `OffscreenCanvas` where it exists, because this can be
 * reached from a context with no DOM, and a `<canvas>` otherwise.
 *
 * The last fallback is for a test runner, which has neither and also has no renderer to upload
 * anything: there, all that matters is that the value is not `null`, because not-null is the whole
 * point.
 */
let disposedTileImageValue: unknown = null;

function disposedTileImage(): unknown {
  if (disposedTileImageValue === null) {
    disposedTileImageValue =
      typeof OffscreenCanvas === 'function'
        ? new OffscreenCanvas(1, 1)
        : typeof document === 'object' && document !== null
          ? Object.assign(document.createElement('canvas'), { width: 1, height: 1 })
          : { width: 1, height: 1 };
  }
  return disposedTileImageValue;
}

/** Shared, immutable per-grid attribute data handed to every tile. See `mesher.ts`. */
export interface SharedTileAttributes {
  uvs: Float32Array;
  indices: Uint16Array | Uint32Array;
}

/** How a tile lets go of the resources it did not create. */
export interface TileResourceOwner {
  /**
   * Called exactly once, from {@link TerrainTile.dispose}, after the tile has released its own
   * GPU resources. The owner hands the material back to the factory that built it and unwinds
   * its accounting — one place, so every disposal path (eviction, replacement, `clear`) is
   * covered by construction rather than by remembering.
   */
  onTileDisposed(tile: TerrainTile): void;
}

/** A resident tile: GPU resources plus everything the simulation needs to query it. */
export class TerrainTile implements CacheableTile {
  readonly key: number;
  readonly zoom: number;
  readonly mesh: Mesh;
  readonly geometry: BufferGeometry;
  readonly texture: Texture | null;
  /** This tile's own material — one per tile, because a material carries one map. */
  readonly material: Material;
  private readonly owner: TileResourceOwner;
  /**
   * The decoded imagery, kept so {@link dispose} can `close()` it.
   *
   * An `ImageBitmap` is a quarter of a megabyte of memory the JS heap cannot see and the
   * collector will not hurry to reclaim, and `Texture.dispose()` releases the GPU texture
   * without touching it. Left unclosed, a long flight leaks one per tile ever loaded.
   */
  private imagery: ImageBitmap | null;

  /** The tile's own tangent frame — the mesh's coordinate system. */
  readonly frame: LocalFrame;
  readonly centreLatitudeDeg: number;
  readonly centreLongitudeDeg: number;
  readonly westDeg: number;
  readonly eastDeg: number;
  readonly southDeg: number;
  readonly northDeg: number;

  /** Height field [m MSL], `GRID_SIZE × GRID_SIZE`, row-major from the north-west corner. */
  readonly heightGrid: Float32Array;
  readonly gridSize: number;
  readonly minHeightM: number;
  readonly maxHeightM: number;
  readonly waterFraction: number;
  readonly hasElevation: boolean;

  readonly byteSize: number;
  /** The part of {@link byteSize} that is JS heap rather than GPU memory [bytes]. */
  readonly heapByteSize: number;

  /** 0 = fully morphed to the coarse surface, 1 = full detail. Driven each frame. */
  morphFactor = 1;


  constructor(
    response: MeshTileResponse,
    material: Material,
    texture: Texture | null,
    imagery: ImageBitmap | null,
    shared: SharedTileAttributes,
    owner: TileResourceOwner,
  ) {
    this.material = material;
    this.owner = owner;
    this.imagery = imagery;
    this.key = response.key;
    this.zoom = keyZoom(response.key);
    const x = keyX(response.key);
    const y = keyY(response.key);

    this.westDeg = tileXToLongitude(x, this.zoom);
    this.eastDeg = tileXToLongitude(x + 1, this.zoom);
    this.northDeg = tileYToLatitude(y, this.zoom);
    this.southDeg = tileYToLatitude(y + 1, this.zoom);
    this.centreLongitudeDeg = tileXToLongitude(x + 0.5, this.zoom);
    this.centreLatitudeDeg = tileYToLatitude(y + 0.5, this.zoom);
    this.frame = new LocalFrame(this.centreLatitudeDeg, this.centreLongitudeDeg, 0);

    this.heightGrid = response.heightGrid;
    this.gridSize = Math.round(Math.sqrt(response.heightGrid.length));
    this.minHeightM = response.minHeightM;
    this.maxHeightM = response.maxHeightM;
    this.waterFraction = response.waterFraction;
    this.hasElevation = response.hasElevation;

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(response.positions, 3));
    geometry.setAttribute('normal', new BufferAttribute(response.normals, 3));
    // UVs and indices are the same bytes for every tile in the world, so the arrays are shared
    // and only the wrapper is per tile — that wrapper is what three keys the GPU buffer and its
    // disposal on, so each tile still owns and releases its own buffer.
    geometry.setAttribute('uv', new BufferAttribute(shared.uvs, 2));
    // The height this vertex has on the next coarser grid; the material blends toward it.
    geometry.setAttribute('morphHeight', new BufferAttribute(response.morphHeights, 1));
    geometry.setIndex(new BufferAttribute(shared.indices, 1));
    // Computed rather than derived from the vertices: the skirt hangs a long way below and
    // would otherwise inflate the bounding sphere enough to defeat frustum culling.
    geometry.boundingSphere = new Sphere(
      new Vector3(0, (response.minHeightM + response.maxHeightM) * 0.5, 0),
      boundingRadiusOf(response),
    );
    this.geometry = geometry;
    this.texture = texture;

    this.mesh = new Mesh(geometry, material);
    this.mesh.name = `tile-${this.zoom}/${x}/${y}`;
    this.mesh.receiveShadow = true;
    this.mesh.castShadow = false; // terrain casting onto terrain is not worth the cascade cost
    this.mesh.matrixAutoUpdate = false;

    // What the JS heap actually pays for this tile: its own arrays, and nothing shared.
    this.heapByteSize =
      response.positions.byteLength +
      response.normals.byteLength +
      response.morphHeights.byteLength +
      response.heightGrid.byteLength;

    // What the LRU budgets against is GPU memory, which is the number nothing in the browser
    // will warn you about — so the shared arrays still count here. Each tile has its own vertex
    // buffer for them even though the CPU copy is shared.
    this.byteSize =
      this.heapByteSize +
      shared.uvs.byteLength +
      shared.indices.byteLength +
      (texture === null ? 0 : estimateTextureBytes(texture));
  }

  /** Is this tile entirely at or below sea level? Then the ocean surface covers it. */
  get isAllWater(): boolean {
    return this.waterFraction >= 1 && this.maxHeightM <= 0;
  }

  /**
   * Bilinear ground height at a geographic point [m MSL].
   * Returns NaN when the point is outside this tile.
   */
  heightAt(latitudeDeg: number, longitudeDeg: number): number {
    const u = (longitudeDeg - this.westDeg) / (this.eastDeg - this.westDeg);
    // Latitude is non-linear in tile space, so invert through the Mercator y rather than
    // lerping between the tile's north and south edges — at low zooms that is kilometres out.
    const v = mercatorFraction(latitudeDeg, this.northDeg, this.southDeg);
    if (!(u >= 0 && u <= 1 && v >= 0 && v <= 1)) return Number.NaN;

    const n = this.gridSize;
    const fx = u * (n - 1);
    const fy = v * (n - 1);
    const x0 = Math.min(Math.floor(fx), n - 2);
    const y0 = Math.min(Math.floor(fy), n - 2);
    const tx = fx - x0;
    const ty = fy - y0;
    const g = this.heightGrid;
    const h00 = g[y0 * n + x0];
    const h10 = g[y0 * n + x0 + 1];
    const h01 = g[(y0 + 1) * n + x0];
    const h11 = g[(y0 + 1) * n + x0 + 1];
    return (h00 * (1 - tx) + h10 * tx) * (1 - ty) + (h01 * (1 - tx) + h11 * tx) * ty;
  }

  /** True when this tile owns a decoded imagery bitmap. Read by the owner's accounting. */
  get hasImagery(): boolean {
    return this.texture !== null;
  }

  private disposed = false;

  dispose(): void {
    if (this.disposed) return; // idempotent: double-disposal must not double-count
    this.disposed = true;

    this.geometry.dispose();
    this.mesh.removeFromParent();

    /*
     * Hand the material back *before* the texture is taken apart, not after.
     *
     * Releasing repoints the material's texture node at the factory's shared placeholder, which is
     * the only reference of ours that still points at this tile's imagery. Doing it first means the
     * teardown below cannot invalidate a texture something of ours is still holding — and the
     * ordering costs nothing, because release is bookkeeping.
     *
     * Through the owner, not `material.dispose()` directly: the factory keeps a set of the
     * materials it built so it can tear them all down, and a set that is only ever added to is a
     * leak that also pins every tile's node graph — and through it every tile's texture.
     */
    this.owner.onTileDisposed(this);

    if (this.texture !== null) {
      this.texture.dispose();
      /*
       * A tombstone image, and **not `null`** — which is what this line used to assign, and which
       * killed the render loop about a minute into every flight.
       *
       * The intent was right: three has to stop pointing at the `ImageBitmap` before `close()`
       * detaches it, or a stale re-upload reads from a detached surface. The implementation
       * replaced one crash with another. `Textures.updateTexture` guards with
       * `if (image === undefined) warn(...) else if (image.complete === false)` — it checks for
       * `undefined` and not for `null`, so a null image goes straight into the second branch and
       * throws `Cannot read properties of null (reading 'complete')`. That throw happens inside
       * `renderer.render()`, and `GameLoop.onFrame` requests its next frame at the *end* of the
       * frame, so the throw is permanent: the simulator stops, silently, still reporting itself
       * visible.
       *
       * It needs a stale reference to reach three at all, and material pooling is what produces
       * one: a pooled material's binding can still name this texture when three next builds a bind
       * group for it (`_createBindings → updateTexture`), even though nothing in the scene graph
       * references it any more — measured, with a holder count of exactly 0 and the first
       * occurrence landing on the first tile evictions of the run.
       *
       * So the texture keeps a valid image that is not the bitmap: one shared 1×1 canvas. A stale
       * upload then costs one pixel and nothing else, which is the correct price for a reference
       * that should not exist.
       */
      this.texture.image = disposedTileImage();
    }
    this.imagery?.close();
    this.imagery = null;
  }
}

export interface TerrainSystemOptions {
  elevation: TileProvider;
  imagery: TileProvider;
  /**
   * Second imagery source, used per tile wherever the first has no coverage.
   *
   * Optional: omitted, the streamer behaves exactly as it did with one source. Present, it is what
   * makes a regional high-resolution layer safe to ship — NAIP is 1 m over the United States and
   * 404s everywhere else, including over water, and the fallback is what keeps that from becoming
   * untextured tiles over a bay.
   */
  imageryFallback?: TileProvider;
  origin: FloatingOrigin;
  settings: QualitySettings;
  /**
   * Builds a tile's material. One per tile rather than one shared: a three material carries a
   * single map, and every tile has its own imagery. The factory reuses the same uniform nodes
   * across them, so the generated shader is identical and three compiles exactly one program.
   */
  createTileMaterial: (map: Texture | null) => Material;
  /**
   * Set one tile material's geomorph weight, 1 = its own surface, 0 = its parent's.
   *
   * Optional so a test can build a terrain system without a renderer; omitted, tiles simply do
   * not morph, which is what the whole world did before v1.1.
   */
  setTileMorph?: (material: Material, morph: number) => void;
  /**
   * How many tile-material node graphs have actually been *built* so far.
   *
   * Optional, and it exists because the obvious counter lies. This system cannot see whether
   * `createTileMaterial` built a graph or took one from a pool — they are the same call — so
   * counting calls reports a pool that is working and a pool that is not identically. The factory
   * knows, so it is asked. Absent, the stat falls back to the request count and is labelled as
   * such. See `render/terrainMaterial.ts`.
   */
  materialsBuilt?: () => number;
  /**
   * Upload a tile's imagery to the GPU now, rather than leaving it to the frame that first draws
   * the tile.
   *
   * `renderer.initTexture()`. Optional, because this system has no renderer and tests have no GPU.
   * Measured on the canyon run: about 0.21 ms per texture here, against roughly 3 ms of the ~10 ms
   * that creating the tile's `RenderObject` costs inside `render()`. It does not make the upload
   * free — it moves it out of the frame that can least afford it and into the streaming step,
   * which is amortised and budgeted for exactly this.
   */
  warmTexture?: (texture: Texture) => void;
  /**
   * Hand a tile's material back when the tile is evicted. Required in practice — a factory that
   * only ever hears about creation accumulates every material the session has ever built.
   */
  releaseTileMaterial: (material: Material) => void;
  /**
   * 16-bit tile indices. Only the WebGL2 fallback wants them: the WebGPU backend converts a
   * `Uint16Array` index buffer to `Uint32Array` on upload and reassigns the attribute's array,
   * so asking for 16-bit there costs an allocation and a copy per tile and saves nothing.
   */
  use16BitIndices?: boolean;
  /** Worker count. Two is plenty: the fetch is the bottleneck, not the meshing. */
  workerCount?: number;
  /** Injected for tests, so nothing here needs a real Worker or a network. */
  createWorker?: () => Worker;
}

export interface TerrainStats {
  selected: number;
  resident: number;
  loading: number;
  queued: number;
  inFlight: number;
  failed: number;
  cacheBytes: number;
  cacheMaxBytes: number;
  evictions: number;
  substituted: number;
  byteCacheAvailable: boolean;
  /** Worker time on the last completed tile, for attributing the streaming budget [ms]. */
  lastFetchMs: number;
  lastDecodeMs: number;
  lastMeshMs: number;

  /** Finished tiles waiting for an upload slot. A standing backlog means the budget is too low. */
  pendingAdoptions: number;
  /** Tiles adopted since start-up. Cumulative, so the perf harness can difference it. */
  tilesAdopted: number;
  /** Bytes handed to the GPU since start-up. Cumulative. */
  uploadedBytes: number;
  /** Tile materials built since start-up. Cumulative. */
  /**
   * Node graphs actually **built** — the expensive kind, a full TSL→WGSL generation inside
   * `renderer.render()`. Falls back to the number of materials *requested* when the factory does
   * not report builds, in which case a pool hit and a pool miss count the same and the figure
   * cannot be read as a cost. See {@link TerrainSystemOptions.materialsBuilt}.
   */
  materialsCreated: number;
  /** Materials asked for, hit or miss. The denominator of the pool's reuse ratio. */
  materialsRequested: number;
  /** Tiles disposed since start-up. Cumulative. */
  tilesDisposed: number;
  /**
   * Materials, textures and bitmaps still alive. In the steady state these track `resident`
   * exactly; any drift between them is the leak this milestone was about, made visible.
   */
  liveMaterials: number;
  liveBitmaps: number;
  /** JS heap held by resident tiles [bytes] — the part of the cache the collector can see. */
  heapBytes: number;
}

const scratchPosition = /* #__PURE__ */ new Vector3();
const scratchRotation = /* #__PURE__ */ new Quaternion();

export class TerrainSystem implements TileResourceOwner {
  readonly group = new Group();
  readonly cache: TileCache<TerrainTile>;
  readonly selector: LodSelector;
  /**
   * The LOD request, reused every frame.
   *
   * `select` is far too large to inline, so an eight-field literal at the call site genuinely
   * escapes and is genuinely allocated — once a frame, for the life of the session. The selection
   * coming back the other way has always been handled this way; this is the matching half.
   */
  private readonly lodRequest: LodSelectionParams = {
    latitudeDeg: 0,
    longitudeDeg: 0,
    altitudeM: 0,
    viewportHeightPx: 1080,
    verticalFovRad: 1,
    geometryErrorPx: 9.5,
    maxDistanceM: 80_000,
    lodBias: 0,
  };

  private readonly elevation: TileProvider;
  private readonly imagery: TileProvider;
  private readonly imageryFallback: TileProvider | null;
  /**
   * This frame's geomorph weight per visible tile.
   *
   * Keyed rather than carried on the tile because the selection is rebuilt every frame and a tile
   * can be dropped and re-pinned; the map is cleared and refilled, so a stale weight cannot
   * survive into a frame where the tile is no longer at its boundary.
   */
  private readonly morphByKey = new Map<number, number>();
  private readonly origin: FloatingOrigin;
  private readonly createTileMaterial: (map: Texture | null) => Material;
  private readonly setTileMorph: (material: Material, morph: number) => void;
  private readonly materialsBuilt: (() => number) | undefined;
  private readonly warmTexture: ((texture: Texture) => void) | undefined;
  private readonly releaseTileMaterial: (material: Material) => void;
  private readonly shared: SharedTileAttributes;
  private readonly workers: Worker[] = [];

  /** Reused by {@link requestMissing}; a tile request must not allocate. */
  private readonly demWindowScratch = makeDemWindow();

  /** Tiles asked for and not yet delivered *or* not yet adopted. */
  private readonly loading = new Set<number>();
  /** Tiles the provider had nothing for, so we stop asking. */
  private readonly unavailable = new Set<number>();
  /** Finished tiles waiting for an upload slot, oldest first. */
  private readonly pending: MeshTileResponse[] = [];

  private tilesAdoptedValue = 0;
  private uploadedBytesValue = 0;
  private materialsRequestedValue = 0;
  private tilesDisposedValue = 0;
  private liveMaterialsValue = 0;
  private liveBitmapsValue = 0;
  private heapBytesValue = 0;

  private settings: QualitySettings;
  private visibleKeys: number[] = [];
  private readonly stats: TerrainStats = {
    selected: 0,
    resident: 0,
    loading: 0,
    queued: 0,
    inFlight: 0,
    failed: 0,
    cacheBytes: 0,
    cacheMaxBytes: 0,
    evictions: 0,
    substituted: 0,
    byteCacheAvailable: false,
    lastFetchMs: 0,
    lastDecodeMs: 0,
    lastMeshMs: 0,
    pendingAdoptions: 0,
    tilesAdopted: 0,
    uploadedBytes: 0,
    materialsCreated: 0,
    materialsRequested: 0,
    tilesDisposed: 0,
    liveMaterials: 0,
    liveBitmaps: 0,
    heapBytes: 0,
  };

  constructor(options: TerrainSystemOptions) {
    this.elevation = options.elevation;
    this.imagery = options.imagery;
    this.imageryFallback = options.imageryFallback ?? null;
    this.origin = options.origin;
    this.createTileMaterial = options.createTileMaterial;
    this.setTileMorph = options.setTileMorph ?? ((): void => {});
    this.materialsBuilt = options.materialsBuilt;
    this.warmTexture = options.warmTexture;
    this.releaseTileMaterial = options.releaseTileMaterial;
    this.settings = options.settings;
    this.shared = {
      uvs: tileUvs(GRID_SIZE),
      indices: tileIndices(GRID_SIZE, options.use16BitIndices ?? false),
    };

    this.group.name = 'terrain';
    // Tiles are placed by hand every frame; three does not need to walk the group.
    this.group.matrixAutoUpdate = false;

    this.cache = new TileCache<TerrainTile>({
      maxBytes: options.settings.terrain.tileCacheMB * 1024 * 1024,
    });
    this.selector = new LodSelector({
      minZoom: 6,
      /*
       * The quadtree goes as deep as the *imagery*, and no longer stops where elevation does.
       *
       * It used to take `min(elevation, imagery)`, and elevation always won: terrarium 404s past
       * z15 while NAIP is a 1 m source good to z17. So the ground was drawn at 3.78 m/px out of a
       * source holding 0.95, and the milestone that added NAIP for its resolution could not reach
       * two thirds of it. The old reasoning was sound — a tile with no heights meshes flat, which
       * is far worse than blurry — but the conclusion did not follow: a tile past the elevation
       * cap does not have to go *without* heights, it can read its ancestor's (see `demWindow`).
       *
       * The imagery term is unchanged in spirit: the deepest zoom guaranteed to produce a texture
       * anywhere on Earth, which is the fallback's cap when there is one, because the primary is
       * regional by definition and cannot be relied on outside its coverage.
       */
      maxZoom:
        this.imageryFallback === null
          ? this.imagery.maxZoom
          : Math.max(this.imagery.maxZoom, this.imageryFallback.maxZoom),
      gridSize: GRID_SIZE,
      maxTiles: maxTilesForCacheBudget(options.settings.terrain.tileCacheMB),
    });

    const createWorker = options.createWorker ?? defaultWorkerFactory;
    const count = Math.max(1, options.workerCount ?? 2);
    for (let i = 0; i < count; i++) {
      const worker = createWorker();
      worker.addEventListener('message', (event: MessageEvent<TileWorkerResponse>) =>
        this.onWorkerMessage(event.data),
      );
      this.workers.push(worker);
    }
  }

  /** True when tiles use 16-bit indices — the WebGL2 fallback's layout. */
  get uses16BitIndices(): boolean {
    return this.shared.indices instanceof Uint16Array;
  }

  /** Re-read the quality tier: cache budget, screen-space error target, draw distance. */
  applyQuality(settings: QualitySettings): void {
    this.settings = settings;
    this.cache.setMaxBytes(settings.terrain.tileCacheMB * 1024 * 1024);
    this.selector.maxTiles = maxTilesForCacheBudget(settings.terrain.tileCacheMB);
  }

  /**
   * Select, request and place tiles for this view.
   *
   * @param latitudeDeg  camera position on the planet
   * @param longitudeDeg camera position on the planet
   * @param altitudeM    camera altitude [m MSL]
   */
  updateFrame(
    latitudeDeg: number,
    longitudeDeg: number,
    altitudeM: number,
    viewportHeightPx: number,
    verticalFovRad: number,
  ): void {
    /*
     * The parameters go through one reused struct rather than a fresh literal.
     *
     * `select` is far too large to inline, so an eight-field object literal here genuinely escapes
     * and is genuinely allocated — every frame, for the life of the session. `LodSelection` on the
     * way back out is already handled exactly this way.
     */
    const request = this.lodRequest;
    request.latitudeDeg = latitudeDeg;
    request.longitudeDeg = longitudeDeg;
    request.altitudeM = altitudeM;
    request.viewportHeightPx = viewportHeightPx;
    request.verticalFovRad = verticalFovRad;
    request.geometryErrorPx = this.settings.terrain.geometryErrorPx;
    request.maxDistanceM = this.settings.drawDistanceM;
    request.lodBias = this.settings.terrain.lodBias;
    const selection = this.selector.select(request);

    this.drainPending();
    this.cache.unpinAll();
    this.resolveVisibleSet(selection);
    this.requestMissing(selection);
    this.placeVisible();
    this.cache.trim();

    this.stats.selected = selection.keys.length;
    this.stats.resident = this.cache.size;
    this.stats.loading = this.loading.size;
    this.stats.pendingAdoptions = this.pending.length;
    const cacheStats = this.cache.statistics();
    this.stats.cacheBytes = cacheStats.bytes;
    this.stats.cacheMaxBytes = cacheStats.maxBytes;
    this.stats.evictions = cacheStats.evictions;
  }

  /**
   * Adopt at most this frame's allowance of finished tiles.
   *
   * The budget is a tile count rather than a byte count on purpose: what actually costs is the
   * fixed per-tile work — buffer creation, texture upload with its mipmap chain, and the
   * first-use pipeline lookup for a material three has not seen before — and that is very nearly
   * constant per tile, because every tile is the same size.
   */
  private drainPending(): void {
    const budget = Math.max(1, this.settings.terrain.tileUploadsPerFrame);
    for (let i = 0; i < budget && this.pending.length > 0; i++) {
      const message = this.pending.shift();
      if (message !== undefined) this.adoptTile(message);
    }
  }

  /**
   * Work out what to actually show, pinning as we go.
   *
   * A wanted tile that has not arrived is replaced by the nearest resident ancestor. That is
   * what makes the ground fill in progressively — blurry first, sharp a moment later — instead
   * of a hole opening under the aircraft every time the camera moves somewhere new.
   */
  private resolveVisibleSet(selection: LodSelection): void {
    const visible = this.visibleKeys;
    visible.length = 0;
    this.morphByKey.clear();
    let substituted = 0;

    for (let i = 0; i < selection.keys.length; i++) {
      const key = selection.keys[i];
      if (this.cache.pin(key)) {
        visible.push(key);
        this.morphByKey.set(key, selection.morphs[i]);
        continue;
      }

      // Walk up to the closest resident ancestor.
      let zoom = keyZoom(key);
      let x = keyX(key);
      let y = keyY(key);
      while (zoom > 0) {
        zoom--;
        x >>= 1;
        y >>= 1;
        const ancestor = tileKey(zoom, x, y);
        if (this.cache.pin(ancestor)) {
          if (!visible.includes(ancestor)) visible.push(ancestor);
          /*
           * A substitute stands in for a tile that has not streamed yet, so it is not at its own
           * LOD boundary and must not be morphed toward its parent — it is already coarser than
           * the selection asked for. Full detail is the honest weight.
           */
          this.morphByKey.set(ancestor, 1);
          substituted++;
          break;
        }
      }
    }
    this.stats.substituted = substituted;
  }

  /** Ask the workers for whatever is selected but not resident. Nearest tiles go first. */
  private requestMissing(selection: LodSelection): void {
    for (let i = 0; i < selection.keys.length; i++) {
      const key = selection.keys[i];
      if (this.cache.has(key) || this.loading.has(key) || this.unavailable.has(key)) continue;

      const zoom = keyZoom(key);
      const x = keyX(key);
      const y = keyY(key);
      // Elevation comes from this tile, or from the deepest ancestor the source publishes.
      const dem = demWindow(zoom, x, y, this.elevation.maxZoom, this.demWindowScratch);
      const request: MeshTileRequest = {
        type: 'mesh',
        key,
        zoom,
        x,
        y,
        demUrl: this.elevation.url(dem.zoom, dem.x, dem.y),
        demU0: dem.u0,
        demV0: dem.v0,
        demSpan: dem.span,
        imageryUrl: zoom <= this.imagery.maxZoom ? this.imagery.url(zoom, x, y) : '',
        imageryFallbackUrl:
          this.imageryFallback !== null && zoom <= this.imageryFallback.maxZoom
            ? this.imageryFallback.url(zoom, x, y)
            : '',
        westDeg: tileXToLongitude(x, zoom),
        eastDeg: tileXToLongitude(x + 1, zoom),
        northDeg: tileYToLatitude(y, zoom),
        southDeg: tileYToLatitude(y + 1, zoom),
        gridSize: GRID_SIZE,
        skirtDepthM: SKIRT_DEPTH_M,
        priority: selection.distancesM[i],
      };

      this.loading.add(key);
      // Shard by key so the same tile always lands on the same worker, and so the two workers
      // get an even split without any bookkeeping.
      this.workers[key % this.workers.length].postMessage(request);
    }
  }

  /**
   * Give every visible tile its transform, and hide the rest.
   *
   * `transformFrom` is exact, so tiles meshed independently in their own frames still line up
   * along their shared edges — which is the whole reason the mesher works in tile-local space.
   */
  private placeVisible(): void {
    // Hide everything first; a tile that dropped out of view this frame must not linger.
    for (const child of this.group.children) child.visible = false;

    for (const key of this.visibleKeys) {
      const tile = this.cache.peek(key);
      if (tile === undefined) continue;

      if (tile.mesh.parent === null) this.group.add(tile.mesh);
      this.origin.frame.transformFromRender(tile.frame, scratchPosition, scratchRotation);
      tile.mesh.position.copy(scratchPosition);
      tile.mesh.quaternion.copy(scratchRotation);
      tile.mesh.updateMatrix();
      tile.mesh.visible = true;
      /*
       * The geomorph weight, written every frame.
       *
       * A uniform assignment per visible tile — a few hundred numbers, no allocation, no shader
       * work. This is the line that was missing: the attribute, the vertex blend and the setter
       * all existed, `TerrainTile.morphFactor` even carried the comment "Driven each frame", and
       * nothing drove it. The blend ran at a constant 1 and every LOD change was a hard snap.
       */
      const morph = this.morphByKey.get(key) ?? 1;
      tile.morphFactor = morph;
      this.setTileMorph(tile.material, morph);
    }
  }

  private onWorkerMessage(message: TileWorkerResponse): void {
    switch (message.type) {
      case 'mesh':
        // Queued rather than adopted: uploading is frame work, and a message handler runs
        // whenever the worker feels like it. The key stays in `loading` until it is adopted, so
        // the selector does not re-request a tile that is already sitting in the queue.
        this.pending.push(message);
        break;
      case 'error':
        this.onTileError(message);
        break;
      case 'stats':
        this.stats.queued = message.queued;
        this.stats.inFlight = message.inFlight;
        this.stats.failed = message.failed;
        this.stats.byteCacheAvailable = message.byteCacheAvailable;
        break;
    }
  }

  private adoptTile(message: MeshTileResponse): void {
    this.loading.delete(message.key);

    let texture: Texture | null = null;
    if (message.imagery !== null) {
      texture = new Texture(message.imagery as unknown as HTMLImageElement);
      texture.needsUpdate = true;
      texture.anisotropy = this.settings.anisotropy;
      texture.generateMipmaps = true;
      // Satellite imagery is authored in sRGB; sampling it as linear washes the world out.
      texture.colorSpace = 'srgb';
      this.liveBitmapsValue++;
    }

    /*
     * Push the imagery to the GPU here, in the streaming step, not in the frame that first draws
     * this tile. See {@link TerrainSystemOptions.warmTexture}.
     */
    if (texture !== null && this.warmTexture !== undefined) this.warmTexture(texture);

    const material = this.createTileMaterial(texture);
    this.materialsRequestedValue++;
    this.liveMaterialsValue++;

    const tile = new TerrainTile(message, material, texture, message.imagery, this.shared, this);
    this.tilesAdoptedValue++;
    this.uploadedBytesValue += tile.byteSize;
    this.heapBytesValue += tile.heapByteSize;
    this.cache.set(message.key, tile);

    this.stats.lastFetchMs = message.fetchMs;
    this.stats.lastDecodeMs = message.decodeMs;
    this.stats.lastMeshMs = message.meshMs;
  }

  /**
   * A tile has let go. Called from {@link TerrainTile.dispose}, which the LRU calls on eviction
   * — the single point where everything a tile was accounted for is unwound.
   */
  onTileDisposed(tile: TerrainTile): void {
    this.tilesDisposedValue++;
    this.liveMaterialsValue--;
    if (tile.hasImagery) this.liveBitmapsValue--;
    this.heapBytesValue -= tile.heapByteSize;
    this.releaseTileMaterial(tile.material);
  }

  private onTileError(message: TileErrorResponse): void {
    this.loading.delete(message.key);
    if (message.cancelled) return;
    // Genuinely unavailable — the edge of coverage, or a source that is down. Remember it, so
    // the selector does not re-request the same missing tile every frame for the rest of the
    // session; the coarser ancestor stands in.
    //
    // Bounded, because "remember forever" is a leak with a polite name: a transcontinental
    // flight along a coastline generates misses without limit. Forgetting the oldest half costs
    // a handful of re-requests that will miss again, which is nothing next to unbounded growth.
    if (this.unavailable.size >= MAX_REMEMBERED_MISSES) {
      let dropped = 0;
      for (const key of this.unavailable) {
        this.unavailable.delete(key); // Sets iterate in insertion order, so this drops the oldest
        if (++dropped >= MAX_REMEMBERED_MISSES / 2) break;
      }
    }
    this.unavailable.add(message.key);
  }

  /**
   * Ground elevation under a geographic point [m MSL], or NaN when nothing has loaded there.
   *
   * Searches from the finest resident tile outward, so the aircraft gets the best data
   * available rather than whatever LOD happens to be drawn. This is what the landing gear
   * raycasts against, so it has to be a lookup with no allocation and no promise in sight.
   */
  sampleGroundHeightM(latitudeDeg: number, longitudeDeg: number): number {
    for (let zoom = this.selector.maxZoom; zoom >= this.selector.minZoom; zoom--) {
      const n = 1 << zoom;
      const x = Math.floor(((longitudeDeg + 180) / 360) * n);
      const latRad = (latitudeDeg * Math.PI) / 180;
      const y = Math.floor(((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n);
      const tile = this.cache.peek(tileKey(zoom, ((x % n) + n) % n, y));
      if (tile === undefined) continue;
      const height = tile.heightAt(latitudeDeg, longitudeDeg);
      if (!Number.isNaN(height)) return height;
    }
    return Number.NaN;
  }

  /**
   * Re-place every tile after the floating origin moved.
   *
   * Cheap by construction: tiles are rigid bodies in their own frames, so this is a transform per
   * **visible** tile — `placeVisible` walks `visibleKeys`, capped by `LodSelector.maxTiles`, not
   * the whole resident LRU — and no geometry is touched. Re-deriving rather than translating is
   * not optional: see the note on {@link OriginRebaseEvent}, a rebase rotates the frame too.
   *
   * Worth being exact about the set, because the 500 m threshold made this function run about
   * twenty times more often per kilometre flown than the 10 km one did. It is still not where the
   * cost of a rebase frame lives — that is the scene pass re-submitting everything that moved,
   * which is why the fix was to move things less far rather than to move fewer of them.
   */
  onOriginRebase(): void {
    this.placeVisible();
  }

  /** Throw away everything and start again, e.g. after spawning at a different airport. */
  reset(): void {
    for (const worker of this.workers) worker.postMessage({ type: 'flush' });
    this.discardPending();
    this.loading.clear();
    this.unavailable.clear();
    this.visibleKeys.length = 0;
    this.cache.clear();
    this.group.clear();
  }

  /**
   * Drop queued tiles that will never be adopted, closing their bitmaps.
   *
   * A teleport across the planet strands whatever the workers had already finished. The typed
   * arrays are ordinary garbage, but an `ImageBitmap` is not: nothing reclaims its backing store
   * until it is closed, so a few airport changes would strand a few hundred megabytes that no
   * heap profiler would attribute to us.
   */
  private discardPending(): void {
    for (const message of this.pending) message.imagery?.close();
    this.pending.length = 0;
    this.stats.pendingAdoptions = 0;
  }

  /**
   * How many tiles of each zoom are actually being drawn, keyed by zoom.
   *
   * Diagnostic only — it allocates, so it is called from the screenshot probe and nowhere near a
   * frame. It reads the *placed* set rather than the selection, which is the distinction that
   * makes it worth having: a selection is what the quadtree asked for, and this is what survived
   * streaming and made it onto the screen.
   */
  drawnZoomHistogram(): Record<number, number> {
    const histogram: Record<number, number> = {};
    for (const key of this.visibleKeys) {
      const zoom = keyZoom(key);
      histogram[zoom] = (histogram[zoom] ?? 0) + 1;
    }
    return histogram;
  }

  statistics(): Readonly<TerrainStats> {
    this.stats.pendingAdoptions = this.pending.length;
    this.stats.tilesAdopted = this.tilesAdoptedValue;
    this.stats.uploadedBytes = this.uploadedBytesValue;
    // The factory's figure when it is available, because that is the one that means "a node graph
    // was generated inside `render()`"; the request count otherwise.
    this.stats.materialsCreated = this.materialsBuilt?.() ?? this.materialsRequestedValue;
    this.stats.materialsRequested = this.materialsRequestedValue;
    this.stats.tilesDisposed = this.tilesDisposedValue;
    this.stats.liveMaterials = this.liveMaterialsValue;
    this.stats.liveBitmaps = this.liveBitmapsValue;
    this.stats.heapBytes = this.heapBytesValue;
    return this.stats;
  }

  dispose(): void {
    for (const worker of this.workers) worker.terminate();
    this.workers.length = 0;
    this.discardPending();
    this.cache.clear();
    this.group.clear();
  }
}

/**
 * Where a latitude sits between a tile's north and south edges, 0…1.
 *
 * Through the Mercator y, not a linear interpolation of latitude: a z6 tile spans several
 * degrees and the projection is distinctly non-linear across it, so lerping puts the ground
 * height sample kilometres from where it belongs.
 */
function mercatorFraction(latitudeDeg: number, northDeg: number, southDeg: number): number {
  const toY = (deg: number): number => {
    const rad = (Math.min(Math.max(deg, -85.05), 85.05) * Math.PI) / 180;
    return Math.asinh(Math.tan(rad));
  };
  const yNorth = toY(northDeg);
  const ySouth = toY(southDeg);
  if (yNorth === ySouth) return 0;
  return (yNorth - toY(latitudeDeg)) / (yNorth - ySouth);
}

function boundingRadiusOf(response: MeshTileResponse): number {
  let maxSq = 0;
  const p = response.positions;
  for (let v = 0; v < p.length; v += 3) {
    const sq = p[v] ** 2 + p[v + 2] ** 2;
    if (sq > maxSq) maxSq = sq;
  }
  const halfHeight = Math.max(1, (response.maxHeightM - response.minHeightM) * 0.5 + SKIRT_DEPTH_M);
  return Math.sqrt(maxSq) + halfHeight;
}

/** RGBA with a full mip chain is 4/3 of the base level. */
function estimateTextureBytes(texture: Texture): number {
  const image = texture.image as { width?: number; height?: number } | null;
  const width = image?.width ?? 256;
  const height = image?.height ?? 256;
  return Math.round(width * height * 4 * 1.34);
}

function defaultWorkerFactory(): Worker {
  return new Worker(new URL('./tile.worker.ts', import.meta.url), { type: 'module' });
}
